import {
  useQuery,
  type QueryFunctionContext,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { useSandboxContext } from '@/providers/SandboxProvider';

/**
 * Query function context augmented with the current sandbox scope, so query
 * functions can build requests without each hook reading the context itself.
 */
export interface AppQueryFunctionContext<TQueryKey extends QueryKey = QueryKey>
  extends QueryFunctionContext<TQueryKey> {
  gameCode: string;
  sandboxCode: string;
}

type AppQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'> & {
  queryFn?: (
    context: AppQueryFunctionContext<TQueryKey>,
  ) => TQueryFnData | Promise<TQueryFnData>;
};

export const useAppQuery = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  const { gameCode, selectedSandbox } = useSandboxContext();

  const scopedQueryKey = [
    gameCode,
    selectedSandbox,
    ...options.queryKey,
  ] as const;

  const { queryFn } = options;

  return useQuery({
    ...options,
    queryKey: scopedQueryKey as unknown as TQueryKey,
    queryFn:
      typeof queryFn === 'function'
        ? (context) =>
            queryFn({ ...context, gameCode, sandboxCode: selectedSandbox })
        : queryFn,
  });
};
